import { BubbleMenuPlugin } from '@tiptap/extension-bubble-menu';
import { ReactRenderer } from '@tiptap/react';
import { Link } from '..';
import type { LinkEditorState } from './LinkEditorBubble';
import { LinkEditorBubble } from './LinkEditorBubble';

declare module '@tiptap/core' {
   interface Commands<ReturnType> {
      editableLink: {
         showLinkEditor: (state: LinkEditorState) => ReturnType;
         hideLinkEditor: () => ReturnType;
         editLink: () => ReturnType;
         forceShow: () => ReturnType;
      };
   }

   // The mark keeps Link's name, so its storage lives at editor.storage.link.
   // The per-editorid renderer entries make up the rest of the keys.
   interface Storage {
      link: { forceShow: boolean } & Record<string, unknown>;
   }
}

export const EditableLink = Link.extend({
   inclusive: false,
   priority: -10,

   onCreate() {
      const doOnCreate = () => {
         const props = { editor: this.editor };

         const editorid = this.editor.storage.editorid.editorid;
         this.storage[`${editorid}-renderer`] = new ReactRenderer(LinkEditorBubble, {
            props,
            editor: this.editor,
         });

         // Float above sticky page chrome (the step editor's z-10 save bar
         // sat over the bubble); 50 matches the shadcn overlay tier.
         const element = this.storage[`${editorid}-renderer`].element as HTMLElement;
         element.style.zIndex = '50';

         this.storage.bubbleMenu = BubbleMenuPlugin({
            pluginKey: `bubble-menu-${editorid}`,
            editor: this.editor,
            element: this.storage[`${editorid}-renderer`].element,
            shouldShow: () => this.editor.isActive('link') || this.storage.forceShow,
            options: { placement: 'bottom' },
            updateDelay: 50,
         });

         this.editor.registerPlugin(this.storage.bubbleMenu);
      };

      const waitForEditorContentReady = () => {
         if (this.editor.view?.dom) {
            doOnCreate();
         } else if (!this.editor.isDestroyed) {
            setTimeout(waitForEditorContentReady, 50);
         }
      };

      waitForEditorContentReady();
   },
   onDestroy() {
      const editorid = this.editor.storage.editorid?.editorid;
      const renderer = this.storage[`${editorid}-renderer`];
      // The bubble-menu plugin view is torn down by editor.destroy(); the
      // ReactRenderer root is ours to unmount.
      renderer?.destroy();
   },
   addCommands() {
      return {
         ...this.parent?.(),
         showLinkEditor:
            (state?: LinkEditorState) =>
            ({ dispatch }) => {
               const editorid = this.editor.storage.editorid.editorid;
               const renderer = this.storage[`${editorid}-renderer`];
               if (dispatch && renderer) {
                  renderer.ref?.show?.(state);
               }
               return true;
            },
         hideLinkEditor:
            () =>
            ({ dispatch }) => {
               const editorid = this.editor.storage.editorid.editorid;
               const renderer = this.storage[`${editorid}-renderer`];
               if (dispatch && renderer) {
                  renderer.ref?.hide?.();
               }
               return true;
            },
         editLink:
            () =>
            ({ dispatch, commands, editor }) => {
               if (!dispatch) {
                  return true;
               }
               commands.extendMarkRange('link');
               if (!editor.isActive('link')) {
                  commands.forceShow();
               }
               commands.showLinkEditor('edit');
               return true;
            },
         forceShow:
            () =>
            ({ dispatch, editor }) => {
               if (!dispatch) {
                  return true;
               }
               this.storage.forceShow = true;
               // For some reason, this is needed to force the bubble to show
               editor.commands.blur();
               // The blur command uses requestAnimationFrame, so we need to wait for that to finish before refocusing.
               requestAnimationFrame(() => {
                  editor.commands.focus();
               });
               return true;
            },
      };
   },
   addStorage() {
      return {
         renderer: null,
         bubbleMenu: null,
         forceShow: false,
      };
   },
   addKeyboardShortcuts() {
      return {
         'Mod-k': () => this.editor.commands.editLink(),
      };
   },
   onSelectionUpdate() {
      this.storage.forceShow = false;
      if (this.editor.isActive('link')) {
         this.editor.commands.showLinkEditor('preview');
      } else {
         this.editor.commands.hideLinkEditor();
      }
   },
   addPasteRules() {
      return [];
   },
}).configure({ openOnClick: false, linkOnPaste: false, autolink: false });
